import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { corporateTheme, corporateLabels } from '../themes/corporate';

const WhyChooseContainer = styled.div`
  width: 100%;
  padding: 6rem 2rem;
  background: ${corporateTheme.backgroundGradient};
  position: relative;
  overflow: hidden;
  font-family: ${corporateTheme.fontFamily};
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const Title = styled(motion.h2)`
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 700;
  color: ${corporateTheme.textPrimary};
  margin-bottom: 1rem;
  font-family: ${corporateTheme.fontFamily};
`;

const Subtitle = styled(motion.p)`
  font-size: 1.2rem;
  color: ${corporateTheme.textSecondary};
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  margin-bottom: 4rem;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const ImageContainer = styled(motion.div)`
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  height: 500px;
  background: linear-gradient(135deg, rgba(30, 64, 175, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%);
  box-shadow: ${corporateTheme.cardShadow};
`;

const StockImage = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(248, 250, 252, 0.7) 0%, rgba(241, 245, 249, 0.6) 100%),
              url('https://images.unsplash.com/photo-1560472354-b33ff0c44a43?ixlib=rb-4.0.3&auto=format&fit=crop&w=1926&q=80');
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  filter: brightness(1.1) contrast(0.8);
`;

const ImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(30, 64, 175, 0.2) 0%, rgba(37, 99, 235, 0.2) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: ${corporateTheme.background};
  background: ${corporateTheme.primaryGradient};
  padding: 1.5rem;
  border-radius: 50px;
  font-weight: 600;
  letter-spacing: 1px;
  min-width: 150px;
  text-align: center;
`;

const ContentSide = styled(motion.div)`
  padding: 2rem;
`;

const BenefitsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const BenefitItem = styled(motion.div)`
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  padding: 2rem;
  background: ${corporateTheme.cardBackground};
  backdrop-filter: blur(10px);
  border-radius: 15px;
  border: 1px solid ${corporateTheme.cardBorder};
  transition: all 0.3s ease;
  box-shadow: ${corporateTheme.cardShadow};
  
  &:hover {
    background: rgba(30, 64, 175, 0.05);
    border-color: rgba(30, 64, 175, 0.2);
    transform: translateX(10px);
  }
`;

const BenefitIcon = styled.div`
  font-size: 1.2rem;
  min-width: 80px;
  text-align: center;
  background: ${corporateTheme.primaryGradient};
  color: ${corporateTheme.background};
  padding: 1rem;
  border-radius: 20px;
  font-weight: 600;
  letter-spacing: 0.5px;
`;

const BenefitContent = styled.div`
  flex: 1;
`;

const BenefitTitle = styled.h3`
  font-size: 1.3rem;
  color: ${corporateTheme.primary};
  margin-bottom: 0.5rem;
  font-weight: 600;
`;

const BenefitDescription = styled.p`
  color: ${corporateTheme.textSecondary};
  line-height: 1.6;
  font-size: 1rem;
`;

const StatsSection = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  background: ${corporateTheme.cardBackground};
  backdrop-filter: blur(20px);
  border-radius: 25px;
  padding: 3rem;
  border: 1px solid ${corporateTheme.cardBorder};
  box-shadow: ${corporateTheme.cardShadow};
`;

const StatItem = styled(motion.div)`
  text-align: center;
  padding: 1.5rem;
`;

const StatNumber = styled.div`
  font-size: 3rem;
  font-weight: 800;
  background: ${corporateTheme.primaryGradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: 1rem;
  color: ${corporateTheme.textSecondary};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const StatDescription = styled.div`
  font-size: 0.9rem;
  color: ${corporateTheme.textTertiary};
  margin-top: 0.5rem;
  line-height: 1.4;
`;

const WhyChooseSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const benefits = [
    {
      icon: "LEADER",
      title: "Industry Leadership",
      description: "Over 5 years of experience in the digital affiliate space with proven track record of success."
    },
    {
      icon: "SUPPORT",
      title: "Professional Support",
      description: "Dedicated account managers and 24/7 technical support to ensure your success."
    },
    {
      icon: "CONVERT",
      title: "Higher Conversions",
      description: "Our optimized funnels and proven strategies deliver conversion rates 30% above industry average."
    },
    {
      icon: "ASIA",
      title: "Asian Market Expertise",
      description: "Deep understanding of Eastern Asian digital markets, customer behavior, and local preferences."
    }
  ];

  const stats = [
    {
      number: "98%",
      label: "Client Satisfaction",
      description: "Happy affiliates who continue working with us"
    },
    {
      number: "45%",
      label: "Max Revenue Share",
      description: "Industry-leading commission rates"
    },
    {
      number: "72H",
      label: "Average Setup Time",
      description: "From signup to first campaign live"
    },
    {
      number: "€2M+",
      label: "Monthly Payouts",
      description: "Total affiliate commissions paid"
    }
  ];

  return (
    <WhyChooseContainer ref={ref} id="why-choose">
      <Container>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <Header>
            <Title variants={itemVariants}>
              Why Choose GrowTech?
            </Title>
            <Subtitle variants={itemVariants}>
              We're not just another {corporateLabels.affiliate} network - we're your strategic partner for long-term success
            </Subtitle>
          </Header>

          <ContentGrid>
            <ImageContainer variants={itemVariants}>
              <StockImage>
                <ImageOverlay>
                  ANALYTICS
                </ImageOverlay>
              </StockImage>
            </ImageContainer>

            <ContentSide variants={itemVariants}>
              <BenefitsList>
                {benefits.map((benefit, index) => (
                  <BenefitItem
                    key={index}
                    variants={itemVariants}
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.3 }}
                  >
                    <BenefitIcon>{benefit.icon}</BenefitIcon>
                    <BenefitContent>
                      <BenefitTitle>{benefit.title}</BenefitTitle>
                      <BenefitDescription>{benefit.description}</BenefitDescription>
                    </BenefitContent>
                  </BenefitItem>
                ))}
              </BenefitsList>
            </ContentSide>
          </ContentGrid>

          <StatsSection variants={itemVariants}>
            {stats.map((stat, index) => (
              <StatItem
                key={index}
                variants={itemVariants}
                whileHover={{ scale: 1.05, y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <StatNumber>{stat.number}</StatNumber>
                <StatLabel>{stat.label}</StatLabel>
                <StatDescription>{stat.description}</StatDescription>
              </StatItem>
            ))}
          </StatsSection>
        </motion.div>
      </Container>
    </WhyChooseContainer>
  );
};

export default WhyChooseSection; 